/**
 * The Query cache, persisted to IndexedDB (plan D17), so the app opens offline
 * with its last-known screens instead of an empty first frame.
 *
 * ── THIS CACHE IS DISPOSABLE BY DESIGN ─────────────────────────────────────
 * Anything here may vanish: the browser evicts IndexedDB under pressure, a
 * private window refuses it, a bundle that changes its shape busts it. So nothing that must
 * survive lives here. The workout outbox (`outbox-runner.ts`, Capacitor
 * Preferences) and the session (secure storage) do not (plan D21).
 */

import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { Query } from "@tanstack/react-query";
import type {
  PersistedClient,
  PersistQueryClientProviderProps,
} from "@tanstack/react-query-persist-client";
import { del, get, set } from "idb-keyval";
import { CACHE_MAX_AGE_MS } from "./query-client";

export const QUERY_CACHE_KEY = "mypocketgym.query-cache";

/** The three calls the persister makes. Promises, as IndexedDB is async. */
export interface AsyncStringStorage {
  getItem(key: string): Promise<string | null>;
  removeItem(key: string): Promise<void>;
  setItem(key: string, value: string): Promise<void>;
}

/**
 * Storage whose failure means "no cache", never a crash.
 *
 * `idb-keyval` rejects in a private window, with site data blocked, or when
 * the quota is gone. A failed read is an empty cache; a failed write or delete
 * is a cache that did not save. Each is logged once per kind, not per save —
 * the persister saves on every cache change.
 */
export function safeStorage(backend: AsyncStringStorage): AsyncStringStorage {
  const warned = new Set<string>();
  const warnOnce = (op: string, error: unknown) => {
    if (!warned.has(op)) {
      warned.add(op);
      console.warn(`[data] query cache ${op} failed`, error);
    }
  };
  return {
    async getItem(key) {
      try {
        return await backend.getItem(key);
      } catch (error) {
        warnOnce("read", error);
        return null;
      }
    },
    async removeItem(key) {
      try {
        await backend.removeItem(key);
      } catch (error) {
        warnOnce("remove", error);
      }
    },
    async setItem(key, value) {
      try {
        await backend.setItem(key, value);
      } catch (error) {
        warnOnce("write", error);
      }
    },
  };
}

/** IndexedDB through idb-keyval's default store. */
const idbStorage: AsyncStringStorage = {
  getItem: async (key) => (await get<string>(key)) ?? null,
  removeItem: (key) => del(key),
  setItem: (key, value) => set(key, value),
};

/**
 * A query is persisted when it HAS data, whatever its status, and never when
 * it is marked `noPersist` (the ws ticket, anything credential-shaped).
 *
 * It used to be `status === "success"`, and that is what defeated this file's
 * own promise. A refetch that fails while offline turns the query's status to
 * `error` and KEEPS its data; the persister saves on every cache change, so
 * the save that followed the first failed refetch wrote a cache without the
 * plan in it. After a relaunch with the API unreachable, Today then drew
 * "Today's workout could not be loaded" from a cache that had held the whole
 * plan one save earlier — for every screen, not only Today.
 *
 * A query with no data is still dropped: `pending` has nothing to keep, and an
 * `error` with nothing behind it restores a screen that must refetch anyway.
 *
 * `serializeForPersist` is the other half of the fix — it is what makes a
 * restored failure read as success-with-stale-data.
 */
export function shouldPersistQuery(
  query: Pick<Query, "meta"> & { state: Pick<Query["state"], "data"> }
): boolean {
  return query.state.data !== undefined && query.meta?.noPersist !== true;
}

/**
 * The invariant every restored query obeys: **restored means success, stale.**
 *
 * `shouldPersistQuery` now keeps a query that failed with data behind it, so
 * without this the next launch would hydrate `status: "error"` into a cache
 * nothing has fetched yet. 34 sites under `features/` read `isError` or
 * `status === "error"`; the ones checked test `data` first, but a query that
 * reports a failure that never happened in this run is a trap for the next
 * caller. Rewriting the status at the boundary keeps the whole app on one
 * rule instead of 34 careful readers.
 *
 * `dataUpdatedAt` is kept, so the query is as stale as it really is and
 * refetches on mount like any other. `error` is dropped, which costs nothing:
 * `ApiError` does not survive `JSON.stringify` either way.
 */
function asStaleSuccess(client: PersistedClient): PersistedClient {
  return {
    ...client,
    clientState: {
      ...client.clientState,
      queries: client.clientState.queries.map((query) => {
        if (query.state.status !== "error" || query.state.data === undefined) {
          return query;
        }
        return {
          ...query,
          state: {
            ...query.state,
            error: null,
            fetchFailureCount: 0,
            fetchFailureReason: null,
            status: "success" as const,
          },
        };
      }),
    },
  };
}

/** An infinite query's data: TanStack's `{ pages, pageParams }`. */
interface InfiniteData {
  readonly pageParams: readonly unknown[];
  readonly pages: readonly unknown[];
}

function isInfinite(data: unknown): data is InfiniteData {
  return (
    typeof data === "object" &&
    data !== null &&
    Array.isArray((data as Partial<InfiniteData>).pages) &&
    Array.isArray((data as Partial<InfiniteData>).pageParams)
  );
}

/**
 * Every infinite query is persisted with its FIRST page only (`D194`).
 *
 * The whole cache is one value, re-serialised on every change and parsed at
 * every launch, and an infinite list kept every page it had ever loaded —
 * the catalogue once per filter combination, the change feed. The first page
 * is what a screen draws on open; the rest are fetched again on the scroll
 * that asks for them, exactly as on the first visit. In memory nothing is
 * trimmed: this is only what reaches the disk.
 */
function firstPagesOnly(client: PersistedClient): PersistedClient {
  return {
    ...client,
    clientState: {
      ...client.clientState,
      queries: client.clientState.queries.map((query) => {
        const { data } = query.state;
        if (!isInfinite(data) || data.pages.length <= 1) {
          return query;
        }
        return {
          ...query,
          state: {
            ...query.state,
            data: {
              pageParams: data.pageParams.slice(0, 1),
              pages: data.pages.slice(0, 1),
            },
          },
        };
      }),
    },
  };
}

/** `asStaleSuccess` and `firstPagesOnly`, then JSON. The persister's
 *  `serialize`. */
export function serializeForPersist(client: PersistedClient): string {
  return JSON.stringify(firstPagesOnly(asStaleSuccess(client)));
}

export function createQueryPersister(backend: AsyncStringStorage = idbStorage) {
  return createAsyncStoragePersister({
    key: QUERY_CACHE_KEY,
    serialize: serializeForPersist,
    storage: safeStorage(backend),
  });
}

export const queryPersister = createQueryPersister();

type PersistOptions = PersistQueryClientProviderProps["persistOptions"];

/**
 * - `maxAge` equals the client's `gcTime` (`query-client.ts`); raising one
 *   without the other drops restored queries.
 * - `buster` is the cache's SHAPE version (`D190`), a hash of every source
 *   that can change what this cache holds (`vite.config.ts`). A cache written
 *   under another shape is thrown away rather than hydrated into code that
 *   expects another one. It was the bundle version until `D190`, which threw
 *   the whole cache away on every OTA update — several a day on development —
 *   so the launch after each one waited on the network behind a blank gate.
 */
export function persistOptionsFor(
  persister: PersistOptions["persister"],
  buster: string
): PersistOptions {
  return {
    buster,
    dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
    maxAge: CACHE_MAX_AGE_MS,
    persister,
  };
}

export const persistOptions = persistOptionsFor(
  queryPersister,
  import.meta.env.VITE_CACHE_SCHEMA ?? "dev"
);
